/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren } from 'preact';
import { DataCellPadding, TableRendererContext, TableRowContext } from '.';
import { FormContext } from '../hooks/UNSAFE_useFormContext';
import { classNames } from '../utils/UNSAFE_classNames';
import { getFormTextAlign, getDataCellClassArray } from '../PRIVATE_Table/utils/TableThemeUtils';

/**
 * Props for the TableDisplayCell Component
 */
export type TableDisplayCellProps<K, D, C> = {
  tableId: string;
  rowData: D;
  columnKey: C;
  columnIndex: number;
  rowKey: K;
  rowIndex: number;
  isFinalRowIndex: boolean;
  isFinalColumnIndex: boolean;
  isFirstLogicalColumnIndex: boolean;
  isFinalLogicalColumnIndex: boolean;
  hasVerticalGridlines: boolean;
  hasHorizontalGridlines: boolean;
  hasFooters: boolean;
  hasVerticalUnderflow: boolean;
  isSticky: boolean;
  isStickyStartEdge: boolean;
  isStickyEndEdge: boolean;
  hasStartEdge: boolean;
  hasEndEdge: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  isAccRowHeader: boolean;
  field?: keyof D;
  renderer?: (context: TableRendererContext<K, D, C>) => ComponentChildren;
  padding?: DataCellPadding<K, D>;
  horizontalAlignment?: 'start' | 'end' | 'left' | 'right' | 'center';
  verticalAlignment?: 'top' | 'center' | 'bottom';
};

function _defaultCellRenderer<K, D, C>({ rowData, field }: TableRendererContext<K, D, C>) {
  return field != null ? String(rowData[field]) : undefined;
}

/**
 * The internal component used to render a single cell in a TableDisplayRow.
 */
export function TableDisplayCell<K, D, C>({
  tableId,
  rowData,
  field,
  columnKey,
  columnIndex,
  rowKey,
  rowIndex,
  isFinalRowIndex,
  isFinalColumnIndex,
  isFirstLogicalColumnIndex,
  isFinalLogicalColumnIndex,
  hasVerticalGridlines,
  hasHorizontalGridlines,
  hasFooters,
  hasVerticalUnderflow,
  isSticky,
  isAccRowHeader,
  renderer = _defaultCellRenderer,
  isStickyStartEdge,
  isStickyEndEdge,
  hasStartEdge,
  hasEndEdge,
  isRtl,
  isHighContrast,
  padding,
  horizontalAlignment = 'start',
  verticalAlignment = 'center'
}: TableDisplayCellProps<K, D, C>) {
  const rowContext: TableRowContext<K, D> = { key: rowKey, data: rowData };
  const columnPadding = typeof padding === 'function' ? padding(rowContext) : padding;
  const hasLastHorizontalGridline = hasHorizontalGridlines && (hasVerticalUnderflow || !hasFooters);

  const cellClasses = classNames(
    getDataCellClassArray({
      isFirstColumnIndex: columnIndex === 0 || isFirstLogicalColumnIndex,
      isFinalColumnIndex: isFinalColumnIndex || isFinalLogicalColumnIndex,
      isFirstRowIndex: rowIndex === 0,
      isFinalRowIndex,
      hasVerticalGridlines,
      hasHorizontalGridlines,
      hasLastHorizontalGridline,
      isSticky,
      hasStartEdge,
      hasEndEdge,
      isStickyStartEdge,
      isStickyEndEdge,
      isRtl,
      isHighContrast,
      horizontalAlignment,
      verticalAlignment,
      padding: columnPadding
    })
  );

  const cellRendererProps = {
    rowData: rowData,
    rowKey: rowKey,
    field: field,
    columnKey: columnKey
  };

  return (
    <td
      aria-colindex={columnIndex + 1}
      class={cellClasses}
      role={isAccRowHeader ? 'rowheader' : 'cell'}
      data-oj-cell-type={'data'}
      data-oj-column-key={columnKey}
      data-oj-table-focusable={tableId}
      data-oj-table-data={tableId}>
      <FormContext.Provider value={{ textAlign: getFormTextAlign(horizontalAlignment, isRtl) }}>
        {renderer(cellRendererProps)}
      </FormContext.Provider>
    </td>
  );
}
